import { createApp } from "vue";
import i18n from "@plugins/i18n";
import EmbedWebsiteModal from "./EmbedWebsiteModal.vue";
import EmbedAppModal from "./EmbedAppModal.vue";
import embedUtils from "../../utils/embed.utils";
import DOMUtils from "@utils/dom.utils";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import richtextUtils from "../../utils/richtext.utils";
import ScrollUtils from "@utils/scroll.utils";
import { isEmpty } from "@/utils/var.check";
import { EMBED_TYPE } from "@modules/editor/service/editor.constant";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import embedComponentEventBus from "@plugins/richtext/plugins/embed-website/service/event-bus";
import AddAppFromLibraryContentSubpage from "@modules/resource-search/AddAppFromLibraryContentSubpage.vue";
import AddWebsiteFromLibraryContentSubpage from "@modules/resource-search/AddWebsiteFromLibraryContentSubpage.vue";
import {
  AddAppFromLibraryContentSubpageSelectionEvent,
  AddWebsiteFromLibraryContentSubpageSelectionEvent,
} from "@modules/resource-search/resource-search-types.ts";

export default function (editor) {
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;

  const SLS_FRAME = "sls-frame";

  const allowedNode = {};
  let allowedUrlPrefix = [];
  let allowedDomainUrls = {};
  let data = null;
  let cleanUpContent = false;

  editor.ui.registry.addMenuButton("embed_website", {
    tooltip: i18n.t("tinymce_plugin_embed_website_menu_button_tooltip"),
    icon: "embed-website",
    fetch: (callback) => {
      const items = [
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_embed_app_button_tooltip"),
          onAction: () => {
            openBrowseAppSubpage();
          },
        },
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_embed_website_button_tooltip"),
          onAction: () => {
            openWebsiteModal();
          },
        },
      ];
      callback(items);
    },
  });

  editor.addCommand("mceEmbedWebsiteDelete", (targetNode) => {
    richtextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".sls-embed-website");
      if (targetNode) editor.execCommand("mceEmbedWebsiteDelete", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  (function init() {
    richTextEditor.plugins["embedWebsite"] = {
      cleanUp,
      containPluginElements,
      renderInEditor,
      openBrowseAppSubpage,
      openWebsiteModal,
      openSettings,
    };

    Promise.all([getWhitelistFrameAttributes(), getWhitelistDomainsMap()]).then(() => {
      // render embed website/app in editor
      renderInlineEmbeddedWebsite();
    });
  })();

  function getWhitelistFrameAttributes() {
    return new Promise((resolve, reject) => {
      embedUtils.loadWhiteList().then((whitelist) => {
        if (!whitelist || !whitelist[SLS_FRAME]) reject();
        const slsFrameWhiteList = whitelist[SLS_FRAME];
        setAllowedNode(SLS_FRAME, slsFrameWhiteList.attributes);
        if (slsFrameWhiteList?.enforcedAttributes?.src?.value) {
          setAllowedUrlPrefix(slsFrameWhiteList.enforcedAttributes.src.value);
        }
        resolve();
      });
    });
  }

  function getWhitelistDomainsMap() {
    return new Promise((resolve, reject) => {
      if (!isEmpty(allowedDomainUrls)) {
        resolve();
      }
      richTextEditor.initWhitelistDomainMapPromise();
      const whitelistDomainMapPromise = richTextEditor.getWhitelistDomainMapPromise;
      whitelistDomainMapPromise.then(
        (data) => {
          allowedDomainUrls = data.data;
          resolve();
        },
        (error) => {
          richTextEditor.passiveApiFailHandler(error);
          reject(error);
        },
      );
    });
  }

  function openBrowseWebsiteSubpage(input) {
    data = input;
    richTextEditor.$contentSubpage.show(
      AddWebsiteFromLibraryContentSubpage,
      {
        baseTitle: [context.lessonTitle],
        actionHidden: () => {
          // On closing subpage without any selection, show the modal
          openWebsiteModal();
        },
      },
      {
        selectionSaved: openWebsiteModalWithSelectedDomain,
      },
    );
  }

  function openWebsiteModal() {
    data = null;
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(embedComponentEventBus.$on("embedItemAdded", handleEmbedAdded));
    richTextEditor.$modal.show(
      EmbedWebsiteModal,
      {
        context: context,
        allowedNode,
        allowedUrlPrefix,
        allowedDomainUrls,
        eventBusWatchInstances,
        presetData: data || null, // null when modal is first opened
      },
      { onHandleBrowseClick: openBrowseWebsiteSubpage },
      true,
    );
  }

  function setEmbedOptions(domain) {
    if (domain.embeddedType === EMBED_TYPE.URL) {
      return "embed-url";
    } else if (domain.embeddedType === EMBED_TYPE.CODE) {
      return "embed-code";
    }
    return "";
  }

  function openWebsiteModalWithSelectedDomain(
    selectedWebsite: AddWebsiteFromLibraryContentSubpageSelectionEvent,
  ) {
    const domain = selectedWebsite.domainSelection;
    selectedWebsite.onSuccess();
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(embedComponentEventBus.$on("embedItemAdded", handleEmbedAdded));
    data.embedType = setEmbedOptions(domain);
    data.urlCode = domain.domainName;

    richTextEditor.$modal.show(
      EmbedWebsiteModal,
      {
        context: context,
        allowedNode,
        allowedUrlPrefix,
        allowedDomainUrls,
        eventBusWatchInstances,
        presetData: data,
      },
      { onHandleBrowseClick: openBrowseWebsiteSubpage },
      true,
    );
  }

  function openAppModal(
    selectedApp: AddAppFromLibraryContentSubpageSelectionEvent,
    gradable?: boolean,
  ) {
    const appData = {
      uuid: selectedApp.appUuid,
    };
    selectedApp.onSuccess();
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(embedComponentEventBus.$on("embedItemAdded", handleEmbedAdded));
    richTextEditor.$modal.show(
      EmbedAppModal,
      {
        context: context,
        allowedNode,
        allowedUrlPrefix,
        eventBusWatchInstances,
        ltiPreviewable: true,
        presetData: appData,
        actionHidden: () => {
          // On closing subpage without any selection, show the modal
          openBrowseAppSubpage(gradable);
        },
      },
      { onHandleBrowseClick: () => openBrowseAppSubpage(gradable) },
      true,
    );
  }

  function openBrowseAppSubpage(gradable?: boolean) {
    richTextEditor.$contentSubpage.show(
      AddAppFromLibraryContentSubpage,
      {
        baseTitle: [context.lessonTitle],
        fromResourceLibrary: true,
        gradable,
      },
      {
        selectionSaved: (selectedApp) => openAppModal(selectedApp, gradable),
      },
    );
  }

  function openSettings(settings) {
    window.tinymce.currentEditor = editor;
    settings.allowedNode = allowedNode;
    settings.allowedUrlPrefix = allowedUrlPrefix;
    settings.allowedDomainUrls = allowedDomainUrls;
    settings.context = context;
    settings.ltiPreviewable = true;
    settings.editorSaveCallback = editorSaveCallback;
    settings.unapprovedUrlCallback = unapprovedUrlHandler;
    cleanUpContent = settings.cleanUpContent === true;
    delete settings.embedConfig.frameAttributes.class;
    delete settings.embedConfig.frameAttributes.name;

    if (settings.embedConfig.type === "embed-app") {
      richTextEditor.$modal.show(
        EmbedAppModal,
        settings,
        {
          onHandleBrowseClick: () => openBrowseAppSubpage(settings.gradable),
        },
        true,
      );
    } else {
      richTextEditor.$modal.show(
        EmbedWebsiteModal,
        settings,
        {
          onHandleBrowseClick: openBrowseWebsiteSubpage,
        },
        true,
      );
    }
  }

  function unapprovedUrlHandler(urlCode) {
    // render unapprove url in hyperlink
    const linkElement = embedUtils.createHyperlink(urlCode);
    editor.insertContent(linkElement.outerHTML);
    editor.save();
    return;
  }

  function handleEmbedAdded(embedData) {
    const type = embedData.type;
    const data = embedData.data;
    let validFrameElement;

    if (data.unapproved) {
      // render unapprove url in hyperlink
      let url = data.urlCode;
      const iframes = embedUtils.getIframes(data.urlCode);
      if (iframes.length) {
        url = iframes[0].getAttribute("src");
      }
      const linkElement = embedUtils.createHyperlink(url);
      editor.insertContent(linkElement.outerHTML);
      editor.save();
      return;
    }

    if (type === "embed-code") {
      const iframes = embedUtils.getIframes(data.urlCode);
      validFrameElement = embedUtils.getValidFrameElement(iframes[0], data, allowedNode);
    } else if (type === "embed-url") {
      validFrameElement = embedUtils.getValidFrameElementByUrl(data);
    } else {
      validFrameElement = embedUtils.getValidFrameElementByAppUrl(data);
    }
    if (type === "embed-app" && cleanUpContent) {
      editor.setContent("");
      cleanUpContent = false;
    }

    editor.insertContent(validFrameElement.outerHTML);
    renderInlineEmbeddedWebsite();
    editor.save();
  }

  function renderInlineEmbeddedWebsite(rerender = false) {
    DOMUtils.elOf(editor.getElement(), ".embed-website-wrapper").forEach((element) => {
      const mediaFrame = element.querySelectorAll(".media-component");
      mediaFrame.forEach((frame) => frame.remove());
      if (!element.querySelector("sls-frame")) return;

      const sls_embed = element.closest(".sls-embed-website");
      if (!sls_embed) {
        const wrapper = DOMUtils.createElement("div", {
          class: "sls-embed-website",
          contenteditable: false,
        });
        element.parentNode.insertBefore(wrapper, element);
        wrapper.appendChild(element);
      } else {
        sls_embed.setAttribute("contenteditable", false);
      }

      // legacy
      DOMUtils.elOf(element, ".embed-caption").forEach((embedCaption) => {
        embedCaption.remove();
      });

      //MOESLSOPS-4723 convert span wrapper to div wrapper
      DOMUtils.elOf(element.parentElement, "span.embed-website-wrapper").forEach(
        (wrapperElement) => {
          const div = DOMUtils.createElement("div", {
            class: "embed-website-wrapper",
          });
          div.innerHTML = wrapperElement.innerHTML;
          wrapperElement.parentNode.replaceChild(div, wrapperElement);
          element = div;
        },
      );
      //MOESLSOPS-4723 convert span wrapper to div wrapper
      DOMUtils.elOf(element.parentElement, "span.embed-website").forEach((wrapperElement) => {
        const div = DOMUtils.createElement("div", {
          class: "embed-website",
        });
        div.innerHTML = wrapperElement.innerHTML;
        wrapperElement.parentNode.replaceChild(div, wrapperElement);
      });

      const slsFrame = element.querySelector("sls-frame");

      const frameAttributes = embedUtils.getValidFrameAttributes(slsFrame.attributes);
      const sanitizedframe = embedUtils.getSanitizedSlsFrame(frameAttributes);
      embedUtils.updateStyleAttribute(sanitizedframe);

      let type = "";
      if (DOMUtils.hasClass(slsFrame, "embed-url")) {
        type = "embed-url";
      } else if (DOMUtils.hasClass(slsFrame, "embed-code")) {
        type = "embed-code";
      } else {
        type = "embed-app";

        const appurl = frameAttributes.appurl ?? "";
        const id = frameAttributes.id ?? "";
        const appuuid = frameAttributes.appuuid ?? "";
        const custom = frameAttributes.custom ?? "";
        if (appuuid) {
          frameAttributes.src = embedUtils.constructAppUrl(
            { id, appurl, appuuid, custom },
            context,
          );
        }
      }
      let mediaComponent = {};
      const mediaApp = createApp(MediaComponent, {
        context: richTextEditor.richTextContext,
        embedConfig: {
          type: type,
          frameAttributes: frameAttributes,
        },
        isInline: true,
        lazyload: false,
        ...AppInstanceUtils.generateDynamicMountProps(parent),
        onEditEmbed: openSettings,
        onMediaLoaded: () => {
          if (!rerender) {
            return;
          }
          editor.selection.select(mediaComponent.$el.closest(".sls-embed-website"));
          ScrollUtils.scrollToTopWithHeaderOffset(mediaComponent.$el, editor.getBody());
        },
      });
      AppInstanceUtils.installCommonPlugin(mediaApp);
      // Mount media component to media element, include the media tag
      mediaComponent = mediaApp.mount(slsFrame);
      DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
      mediaComponent.$el.parentNode.append(sanitizedframe);

      // add <br> if last item is media attachment, so user can add cursor after
      const mediaWrapper = mediaComponent.$el.closest(".sls-embed-website");
      richtextUtils.addNewLineIfIsLastElement(mediaWrapper);
      // let parentParagraph = mediaWrapper.closest("div.paragraph");
      // if (!parentParagraph.nextSibling) {
      //   // let lineElement = document.createElement("br");
      //   // lineElement.setAttribute("data-mce-bogus", 1);
      //   let paragraph = DOMUtils.createElement("div", { class: "paragraph" });
      //   paragraph.innerHTML = "&nbsp;";
      //   parentParagraph.parentNode.insertBefore(paragraph, parentParagraph.nextSibling);
      // }
    });
  }

  function renderInEditor(rerender = false) {
    renderInlineEmbeddedWebsite(rerender);
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".embed-website-wrapper, sls-embed-website").length > 0;
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    container.innerHTML = content;
    const clone = container.cloneNode(true);

    DOMUtils.elOf(clone, ".sls-embed-website").forEach((element) => {
      cleanUpElement(element);
    });

    // DOMUtils.elOf(clone, "div.sls-frame").forEach(frame => {
    //   let cloneFrame = frame.cloneNode(true);
    //   let frameString = cloneFrame.outerHTML;
    //   frameString = frameString.replace(/<div/gi, "<sls-frame"); // start tag
    //   frameString = frameString.replace(/div>/gi, "sls-frame>"); // end tag
    //   frameString = frameString.replace(/data-style/gi, "style");
    //   let div = DOMUtils.createElement("div");
    //   div.innerHTML = frameString;
    //   frame.replaceWith(div.firstChild);
    // });

    return clone.innerHTML;
  }

  function cleanUpElement(element) {
    element.removeAttribute("contenteditable");
    DOMUtils.elOf(element, ".embed-website .media-component").forEach((mediaComponent) => {
      mediaComponent.remove();
    });
    DOMUtils.elOf(element, "sls-frame").forEach((slsFrame) => {
      // restore correct style attributes
      const styleAttribute = slsFrame.getAttribute("data-style");
      if (styleAttribute) {
        slsFrame.removeAttribute("data-style");
        slsFrame.setAttribute("style", styleAttribute);
      }
    });
    // legacy
    DOMUtils.elOf(element, ".embed-caption").forEach((embedCaption) => {
      embedCaption.remove();
    });
  }

  function editorSaveCallback() {
    if (window.tinymce && window.tinymce.currentEditor) {
      window.tinymce.currentEditor.save();
      delete window.tinymce.currentEditor;
    }
  }

  function setAllowedNode(tag, attributes) {
    if (attributes) {
      allowedNode[tag] = attributes.split(",");
    }
  }

  function setAllowedUrlPrefix(urls) {
    if (urls) {
      allowedUrlPrefix = urls.split(",");
    }
  }

  function handlePluginRerender(target) {
    const mediaComponent = target.closest(".media-component");
    if (!mediaComponent || mediaComponent.length === 0 || mediaComponent.__vue__) return;

    const targetNode = mediaComponent.closest(".sls-embed-website");
    if (!targetNode || targetNode.length === 0) return;

    cleanUpElement(targetNode);
    renderInEditor(true);
  }
}
